import React, { useState, useEffect } from 'react';
import { database } from '../firebaseConfig';
import { ref, get } from 'firebase/database';
import { authenticatedFetch } from '../utils/cloudFunctions';
import './WorldDbMigrationModal.css';

const REGION = 'us-central1';
const PROJECT_ID = 'bonusclub-ab5e2';

const WorldDbMigrationModal = ({ userId, onMigrationComplete }) => {
  const [migrationStatus, setMigrationStatus] = useState('idle'); // idle, loading, migrating, success, error
  const [progress, setProgress] = useState(0);
  const [totalContacts, setTotalContacts] = useState(0);
  const [migratedContacts, setMigratedContacts] = useState(0);
  const [error, setError] = useState(null);
  const [databaseData, setDatabaseData] = useState(null);

  useEffect(() => {
    if (!userId) return;
    const loadDatabaseData = async () => {
      try {
        const dbRef = ref(database, `/World/${userId}/db`);
        const snapshot = await get(dbRef);
        if (snapshot.exists()) {
          setDatabaseData(snapshot.val());
          setTotalContacts(Object.keys(snapshot.val()).length);
        }
      } catch (err) {
        console.error('Fehler beim Laden der Daten:', err);
      }
    };
    loadDatabaseData();
  }, [userId]);

  useEffect(() => {
    let intervalId;
    if (migrationStatus === 'loading') {
      // Simuliere fortschreitenden Progress bis max 90%
      intervalId = setInterval(() => {
        setProgress((prev) => (prev < 90 ? prev + 1 : prev));
      }, 100);
    } else if (migrationStatus === 'success' || migrationStatus === 'error') {
      setProgress(100);
    }
    return () => clearInterval(intervalId);
  }, [migrationStatus]);

  const downloadDatabase = () => {
    if (!databaseData) return;
    const dataStr = JSON.stringify(databaseData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `world-db-backup-${userId}-${new Date().toISOString().split('T')[0]}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const startMigration = async () => {
    setMigrationStatus('loading');
    setError(null);
    setProgress(0);
    setMigratedContacts(0);
    try {
      // Cloud Function URL (jetzt mit us-central1)
      const url = `https://${REGION}-${PROJECT_ID}.cloudfunctions.net/migrateWorldDbToFirestore?userId=${userId}`;
      const response = await authenticatedFetch(url, { method: 'POST' });
      const text = await response.text();
      if (response.ok) {
        setMigrationStatus('success');
        setProgress(100);
        setMigratedContacts(totalContacts);
        if (onMigrationComplete) onMigrationComplete();
      } else {
        setMigrationStatus('error');
        setError(text);
      }
    } catch (err) {
      setMigrationStatus('error');
      setError(err.message);
    }
  };

  return (
    <div className="world-db-migration-modal">
      <div className="migration-content">
        <h2>Database Migration Required</h2>
        <p>
          We need to migrate your contacts to our new database system. 
          This process will ensure better performance and reliability.
        </p>
        {migrationStatus === 'idle' && (
          <div className="migration-actions">
            <button 
              className="migration-button"
              onClick={startMigration}
            >
              Start Migration
            </button>
            <button 
              className="download-button"
              onClick={downloadDatabase}
              disabled={!databaseData}
            >
              Download Database Backup
            </button>
          </div>
        )}
        {migrationStatus === 'success' && (
          <div className="migration-success">
            <div className="success-icon">✓</div>
            <p>Migration completed successfully!</p>
          </div>
        )}
        {migrationStatus === 'error' && (
          <div className="migration-error">
            <p>Error during migration: {error}</p>
            <button 
              className="retry-button"
              onClick={startMigration}
            >
              Retry Migration
            </button>
          </div>
        )}
        {migrationStatus === 'loading' && (
          <div className="migration-loading">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <p style={{marginTop: '12px'}}>Migrating contacts... {progress}%</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorldDbMigrationModal; 